// ===========================================
// EMAIL TEMPLATES PAGE – Production Ready
// Tripnovac Sales & Marketing Platform
// Real data only – no mocks or fallbacks
// ===========================================

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail,
  Plus,
  Copy,
  Trash2,
  Edit,
  Eye,
  FileText,
  Layers,
  Star,
  Search,
  Sparkles,
  Clock,
  RefreshCw,
  LayoutTemplate,
  Wand2,
  AlertTriangle,
  X,
  Send,
} from 'lucide-react';
import {
  HDPageHero,
  heroBackgrounds,
  Card,
  Button,
  Modal,
  LoadingSpinner,
} from '../../components/ui';
import { emailService, EmailTemplate, TemplatesListResponse } from '../../services/email.service';
import { useNotify } from '../../store';

// ─── Category Config ──────────────────────────────────────────────────────────

export const CATEGORIES = [
  { value: 'MARKETING',       label: 'Marketing' },
  { value: 'NEWSLETTER',      label: 'Newsletter' },
  { value: 'PROMOTIONAL',     label: 'Promotional' },
  { value: 'TRANSACTIONAL',   label: 'Transactional' },
  { value: 'AUTOMATION',      label: 'Automation' },
  { value: 'TRAVEL_SPECIFIC', label: 'Travel Specific' },
];

const categoryConfig: Record<string, { gradient: string; badge: string; icon: React.ReactNode }> = {
  PROMOTIONAL:     { gradient: 'from-violet-500 to-fuchsia-500', badge: 'bg-violet-100 text-violet-700',   icon: <Star className="w-6 h-6" /> },
  promotional:     { gradient: 'from-violet-500 to-fuchsia-500', badge: 'bg-violet-100 text-violet-700',   icon: <Star className="w-6 h-6" /> },
  NEWSLETTER:      { gradient: 'from-blue-500 to-cyan-500',      badge: 'bg-blue-100 text-blue-700',       icon: <FileText className="w-6 h-6" /> },
  newsletter:      { gradient: 'from-blue-500 to-cyan-500',      badge: 'bg-blue-100 text-blue-700',       icon: <FileText className="w-6 h-6" /> },
  TRANSACTIONAL:   { gradient: 'from-emerald-500 to-teal-500',   badge: 'bg-emerald-100 text-emerald-700', icon: <Mail className="w-6 h-6" /> },
  transactional:   { gradient: 'from-emerald-500 to-teal-500',   badge: 'bg-emerald-100 text-emerald-700', icon: <Mail className="w-6 h-6" /> },
  MARKETING:       { gradient: 'from-amber-500 to-orange-500',   badge: 'bg-amber-100 text-amber-700',     icon: <Sparkles className="w-6 h-6" /> },
  marketing:       { gradient: 'from-amber-500 to-orange-500',   badge: 'bg-amber-100 text-amber-700',     icon: <Sparkles className="w-6 h-6" /> },
  AUTOMATION:      { gradient: 'from-rose-500 to-pink-500',      badge: 'bg-rose-100 text-rose-700',       icon: <Wand2 className="w-6 h-6" /> },
  TRAVEL_SPECIFIC: { gradient: 'from-sky-500 to-indigo-500',     badge: 'bg-sky-100 text-sky-700',         icon: <Send className="w-6 h-6" /> },
  general:         { gradient: 'from-gray-400 to-gray-500',      badge: 'bg-gray-100 text-gray-700',       icon: <Layers className="w-6 h-6" /> },
};

const getCfg = (category: string) => categoryConfig[category] || categoryConfig.general;

// ─── Template Card ────────────────────────────────────────────────────────────

const TemplateCard: React.FC<{
  template: EmailTemplate;
  onPreview: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}> = ({ template, onPreview, onEdit, onDuplicate, onDelete }) => {
  const cfg = getCfg(template.category);

  return (
    <div className="group bg-white rounded-2xl border border-gray-100 hover:border-violet-200 hover:shadow-xl transition-all overflow-hidden">
      <div className="relative h-48 overflow-hidden bg-gray-50">
        {template.thumbnail ? (
          <img src={template.thumbnail} alt={template.name} className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className={`w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br ${cfg.gradient} flex items-center justify-center text-white shadow-lg`}>
                {cfg.icon}
              </div>
              <p className="mt-3 text-sm text-gray-400 font-medium capitalize">
                {template.category.replace('_', ' ').toLowerCase()} template
              </p>
            </div>
          </div>
        )}

        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col items-center justify-end pb-6">
          <div className="flex gap-2 mb-3">
            <button onClick={onPreview} className="px-4 py-2 bg-white rounded-lg text-gray-700 font-medium hover:bg-violet-50 transition-colors flex items-center gap-2 text-sm">
              <Eye className="w-4 h-4" /> Preview
            </button>
            <button onClick={onEdit} className="px-4 py-2 bg-violet-600 rounded-lg text-white font-medium hover:bg-violet-700 transition-colors flex items-center gap-2 text-sm">
              <Edit className="w-4 h-4" /> Edit
            </button>
          </div>
          <div className="flex gap-2">
            <button onClick={onDuplicate} title="Duplicate" className="p-2 bg-white/90 rounded-lg text-gray-700 hover:bg-white transition-colors">
              <Copy className="w-4 h-4" />
            </button>
            <button onClick={onDelete} title="Delete" className="p-2 bg-white/90 rounded-lg text-red-600 hover:bg-red-50 transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="absolute top-4 right-4">
          <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${cfg.badge} capitalize`}>
            {template.category.replace('_', ' ').toLowerCase()}
          </span>
        </div>
      </div>

      <div className="p-5">
        <h3 className="font-bold text-gray-900 truncate group-hover:text-violet-700 transition-colors">{template.name}</h3>
        <p className="text-sm text-gray-500 truncate mt-1">{template.subject || 'No subject set'}</p>
        <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-100">
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{template.usageCount || 0} used</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(template.updatedAt || template.createdAt).toLocaleDateString()}</span>
          </div>
          {template.unlayerJson && (
            <span className="text-xs font-medium text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full">Visual Editor</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────

const DeleteConfirmModal: React.FC<{
  template: EmailTemplate | null;
  onConfirm: () => void;
  onCancel: () => void;
  deleting: boolean;
}> = ({ template, onConfirm, onCancel, deleting }) => (
  <Modal
    isOpen={!!template}
    onClose={onCancel}
    title="Delete Template"
    description="This action cannot be undone"
    size="sm"
    footer={
      <>
        <Button variant="ghost" onClick={onCancel} disabled={deleting}>Cancel</Button>
        <Button variant="primary" onClick={onConfirm} loading={deleting}
          className="bg-red-600 hover:bg-red-700 focus:ring-red-500">
          Delete Template
        </Button>
      </>
    }
  >
    <div className="flex items-start gap-4">
      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
        <AlertTriangle className="w-6 h-6 text-red-600" />
      </div>
      <div>
        <p className="text-gray-700">Are you sure you want to delete <strong>"{template?.name}"</strong>?</p>
        <p className="text-sm text-gray-500 mt-1">All associated send history will also be removed.</p>
      </div>
    </div>
  </Modal>
);

// ─── Preview Modal ────────────────────────────────────────────────────────────

const PreviewModal: React.FC<{
  template: EmailTemplate | null;
  onClose: () => void;
  onEdit: () => void;
}> = ({ template, onClose, onEdit }) => (
  <Modal
    isOpen={!!template}
    onClose={onClose}
    title={template?.name || 'Preview'}
    description={template?.subject}
    size="full"
    footer={
      <>
        <Button variant="ghost" onClick={onClose} icon={<X className="w-4 h-4" />}>Close</Button>
        <Button variant="primary" onClick={onEdit} icon={<Edit className="w-4 h-4" />}>Open in Editor</Button>
      </>
    }
  >
    <div className="bg-gray-100 rounded-xl p-4 min-h-[500px] overflow-auto">
      {template?.htmlContent ? (
        <div className="bg-white rounded-lg shadow-sm p-6 max-w-2xl mx-auto" dangerouslySetInnerHTML={{ __html: template.htmlContent }} />
      ) : (
        <div className="flex items-center justify-center h-64 text-gray-400">
          <div className="text-center"><FileText className="w-12 h-12 mx-auto mb-3" /><p>No preview available</p></div>
        </div>
      )}
    </div>
  </Modal>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const EmailsPage: React.FC = () => {
  const navigate = useNavigate();
  const notify = useNotify();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EmailTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const result = await emailService.listTemplates({ page: 1, limit: 100 });
      setData(result);
    } catch {
      notify.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleDuplicate = async (template: EmailTemplate) => {
    try {
      await emailService.duplicateTemplate(template.id, `${template.name} (Copy)`);
      notify.success('Template duplicated');
      fetchTemplates();
    } catch {
      notify.error('Failed to duplicate template');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await emailService.deleteTemplate(deleteTarget.id);
      notify.success('Template deleted');
      setDeleteTarget(null);
      fetchTemplates();
    } catch {
      notify.error('Failed to delete template');
    } finally {
      setDeleting(false);
    }
  };

  // Normalise response: some API wrappers return { data: { templates } }
  const templates: EmailTemplate[] =
    data?.templates ??
    data?.data?.templates ??
    [];

  const filtered = templates.filter((t) => {
    const search = searchQuery.toLowerCase();
    const matchesSearch = t.name.toLowerCase().includes(search) || (t.subject || '').toLowerCase().includes(search);
    const matchesCategory = filterCategory === 'all' || t.category.toUpperCase() === filterCategory.toUpperCase();
    return matchesSearch && matchesCategory;
  });

  const stats = {
    total:         templates.length,
    promotional:   templates.filter((t) => t.category.toUpperCase() === 'PROMOTIONAL').length,
    newsletter:    templates.filter((t) => t.category.toUpperCase() === 'NEWSLETTER').length,
    avgOpenRate:   data?.avgOpenRate ?? data?.data?.avgOpenRate ?? 0,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <HDPageHero
        title="Email Templates"
        subtitle="Design Studio"
        backgroundImage={heroBackgrounds.emails}
        icon={<Mail className="w-6 h-6" />}
        badge={{ text: 'Template Gallery', variant: 'purple' }}
        variant="compact"
        className="!min-h-[160px]"
        primaryAction={{ label: 'New Template', icon: <Plus className="w-5 h-5" />, onClick: () => navigate('/emails/editor/new') }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 -mt-4">
        {/* Toolbar */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input type="text" placeholder="Search templates…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2.5 w-64 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 transition-all outline-none text-sm" />
            </div>
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2.5 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 transition-all outline-none text-sm bg-white">
              <option value="all">All Categories</option>
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <Button variant="ghost" size="sm" icon={<RefreshCw className="w-4 h-4" />} onClick={fetchTemplates}>Refresh</Button>
          </div>
          <span className="text-sm text-gray-500">{filtered.length} templates</span>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-24">
            <LoadingSpinner size="lg" variant="plane" message="Loading templates…" />
          </div>
        ) : filtered.length === 0 ? (
          <Card variant="default" padding="xl">
            <div className="text-center py-20">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-violet-100 to-fuchsia-100 flex items-center justify-center">
                <Mail className="w-12 h-12 text-violet-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {searchQuery || filterCategory !== 'all' ? 'No matching templates' : 'No templates yet'}
              </h3>
              <p className="text-gray-500 mb-8 max-w-md mx-auto">
                {searchQuery || filterCategory !== 'all'
                  ? 'Clear the search or filter to see all templates.'
                  : 'Create your first email template using the visual drag-and-drop editor.'}
              </p>
              {!searchQuery && filterCategory === 'all' && (
                <Button variant="primary" size="lg" icon={<Plus className="w-5 h-5" />} onClick={() => navigate('/emails/editor/new')} glow>
                  Create Your First Template
                </Button>
              )}
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onPreview={() => setPreviewTemplate(template)}
                onEdit={() => navigate(`/emails/editor/${template.id}`)}
                onDuplicate={() => handleDuplicate(template)}
                onDelete={() => setDeleteTarget(template)}
              />
            ))}
          </div>
        )}

        {/* AI Banner */}
        <div className="mt-10 p-6 rounded-2xl bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 text-white">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <Wand2 className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg">AI Email Generator</h3>
              <p className="text-sm text-white/80">Describe your campaign and let AI create a professional template — then refine it in the visual editor.</p>
            </div>
            <Button variant="secondary" className="bg-white text-violet-700 hover:bg-white/90 flex-shrink-0" onClick={() => navigate('/ai')}>
              Try AI Generator
            </Button>
          </div>
        </div>
      </div>

      {/* Modals */}
      <PreviewModal
        template={previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        onEdit={() => { if (previewTemplate) navigate(`/emails/editor/${previewTemplate.id}`); setPreviewTemplate(null); }}
      />
      <DeleteConfirmModal
        template={deleteTarget}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        deleting={deleting}
      />
    </div>
  );
};

export default EmailsPage;
